import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import { Plus, Shield, User as UserIcon, Pencil, Trash2, ChevronDown } from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";
import confirmAlert from "../../utils/confirmAlert";

import {
  PageHeader,
  Button,
  IconButton,
  Tooltip,
  Badge,
  DataTable,
  UserAvatar,
  EmptyState,
  DropdownMenu,
} from "../../design-system";

class UsersList extends Component {
  constructor(props) {
    super(props);
    this.state = { users: [], loading: true };
  }

  componentDidMount() {
    this.loadUsers();
  }

  loadUsers = async () => {
    try {
      this.setState({ loading: true });
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.get("/api/users");
      if (response.data.success) {
        this.setState({ users: response.data.users || [], loading: false });
      }
    } catch (error) {
      console.error("Error loading users:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur", message: "Impossible de charger les utilisateurs",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  handleChangeRole = async (user, newRole) => {
    if (user.role === newRole) return;
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      await axios.put(`/api/users/${user.id}`, {
        name: user.name,
        email: user.email,
        role: newRole,
      });
      this.setState((s) => ({
        users: s.users.map((u) => (u.id === user.id ? { ...u, role: newRole } : u)),
      }));
      Store.addNotification({
        title: newRole === "ADMIN" ? "Passé administrateur" : "Passé utilisateur",
        message: `« ${user.name || user.email} » a maintenant le rôle ${newRole}.`,
        type: "success", insert: "top", container: "top-right", dismiss: { duration: 2500 },
      });
    } catch (error) {
      console.error("Error changing role:", error);
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Impossible de changer le rôle",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3500 },
      });
    }
  };

  handleDelete = (user) => {
    confirmAlert({
      title: "Supprimer l'utilisateur",
      message: `« ${user.name || user.email} » sera supprimé définitivement.`,
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("jwtToken");
          if (token) setAuthToken(token);
          const response = await axios.delete(`/api/users/${user.id}`);
          if (response.data.success) {
            Store.addNotification({
              title: "Utilisateur supprimé",
              message: `« ${user.name || user.email} » a été retiré.`,
              type: "success",
              insert: "top", container: "top-right", dismiss: { duration: 3000 },
            });
            this.loadUsers();
          }
        } catch (error) {
          console.error("Error deleting user:", error);
          Store.addNotification({
            title: "Erreur",
            message: error.response?.data?.message || "Erreur lors de la suppression",
            type: "danger",
            insert: "top", container: "top-right", dismiss: { duration: 5000 },
          });
        }
      },
    });
  };

  buildColumns() {
    return [
      {
        key: "name", header: "Nom", sortable: true,
        render: (row) => (
          <div className="flex items-center gap-2.5">
            <UserAvatar user={row} size="sm" />
            <div className="leading-tight">
              <div className="font-semibold text-text">{row.name || row.email}</div>
              <div className="text-[11.5px] text-mute">{row.email}</div>
            </div>
          </div>
        ),
      },
      {
        key: "role", header: "Rôle", width: 200,
        render: (row) => (
          <span data-no-row-click>
            <DropdownMenu
              align="start"
              trigger={
                <button type="button" className="inline-flex items-center gap-1 group" aria-label="Changer le rôle">
                  {row.role === "ADMIN"
                    ? <Badge variant="success" leadingIcon={<Shield className="w-3 h-3" strokeWidth={2} />}>Administrateur</Badge>
                    : <Badge variant="neutral" leadingIcon={<UserIcon className="w-3 h-3" strokeWidth={2} />}>Utilisateur</Badge>}
                  <ChevronDown className="w-3 h-3 text-mute group-hover:text-text transition-colors" strokeWidth={2} />
                </button>
              }
            >
              {({ close }) => (
                <>
                  <DropdownMenu.Label>Rôle</DropdownMenu.Label>
                  <DropdownMenu.Item icon={<Shield />} onSelect={() => { this.handleChangeRole(row, "ADMIN"); close(); }}>
                    Administrateur
                    {row.role === "ADMIN" && <span className="ml-auto text-xs text-mute">actuel</span>}
                  </DropdownMenu.Item>
                  <DropdownMenu.Item icon={<UserIcon />} onSelect={() => { this.handleChangeRole(row, "USER"); close(); }}>
                    Utilisateur
                    {row.role === "USER" && <span className="ml-auto text-xs text-mute">actuel</span>}
                  </DropdownMenu.Item>
                </>
              )}
            </DropdownMenu>
          </span>
        ),
      },
      {
        key: "createdAt", header: "Créé le", sortable: true, width: 140,
        render: (row) => (
          <span className="font-mono text-xs text-mute">
            {row.createdAt ? new Date(row.createdAt).toLocaleDateString("fr-FR", {
              year: "numeric", month: "short", day: "numeric",
            }) : "—"}
          </span>
        ),
      },
      {
        key: "actions", header: "", width: 100,
        render: (row) => (
          <div className="flex items-center gap-0.5 justify-end" data-no-row-click>
            <Tooltip content="Modifier">
              <IconButton as={Link} to={`/dashboard/users/${row.id}`}
                size="sm" variant="ghost" icon={<Pencil strokeWidth={1.8} />}
                aria-label={`Modifier ${row.name || row.email}`} />
            </Tooltip>
            <Tooltip content="Supprimer">
              <IconButton size="sm" variant="ghost" icon={<Trash2 strokeWidth={1.8} />}
                aria-label={`Supprimer ${row.name || row.email}`}
                onClick={() => this.handleDelete(row)} />
            </Tooltip>
          </div>
        ),
      },
    ];
  }

  render() {
    const { users, loading } = this.state;

    return (
      <AdminPageShell pageTitle="Utilisateurs">
        <div className="space-y-6 lg:space-y-7">
          <PageHeader
            size="hero"
            eyebrow="— Section vii · Utilisateurs"
            title={<>Qui a la <span className="font-serif italic font-normal align-baseline">main.</span></>}
            description="Comptes ayant accès à l'admin. Modifie leurs droits ou supprime-les."
            actions={
              <Button as={Link} to="/dashboard/users/new"
                leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}>
                Nouvel utilisateur
              </Button>
            }
          />

          {!loading && users.length === 0 ? (
            <EmptyState
              title="Aucun utilisateur"
              description="Crée le premier compte pour donner accès à l'admin."
              action={
                <Button as={Link} to="/dashboard/users/new" leftIcon={<Plus className="w-4 h-4" />}>
                  Nouvel utilisateur
                </Button>
              }
            />
          ) : (
            <DataTable
              data={users}
              columns={this.buildColumns()}
              rowKey="id"
              isLoading={loading}
              variant="duotone"
              showRowIndex
            />
          )}
        </div>
      </AdminPageShell>
    );
  }
}

export default withRouter(UsersList);
